import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../DoctorForm/DoctorForm.css";
import Avatar from "../../assets/avatar.png";
import axios from "axios";
import CustomModal from "../../components/modal/Modal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LocationPicker from "../../components/locationPicker/LocationPicker";
import { MapPin } from "lucide-react";
import { apiUrl } from "../../config/appConfig";

const PhysiotherapyForm = () => {
  const [fullName, setFullName] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [centerType, setCenterType] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [area, setArea] = useState("");
  const [location, setLocation] = useState("");
  const [locationError, setLocationError] = useState("");
  const [onlineConsult, setOnlineConsult] = useState("No");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const fileInputRef = useRef(null);
  const [countryCode, setCountryCode] = useState("+964");
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [profilePicError, setProfilePicError] = useState("");
  const [hasCamera, setHasCamera] = useState(true);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const sessionTokenRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [region, setRegion] = useState("Baghdad");

  // Iraqi regions list
  const iraqiRegions = [
    "Baghdad",
    "Basra",
    "Al-Anbar",
    "Babil (Babylon)",
    "Dhi Qar",
    "Al-Qadisiyyah (Diwaniyah)",
    "Diyala",
    "Dohuk",
    "Erbil",
    "Sulaymaniyah",
    "Halabja",
    "Kirkuk",
    "Karbala",
    "Najaf",
    "Nineveh (Mosul)",
    "Salah Al-Din",
    "Wasit",
    "Maysan",
    "Al-Muthanna"
  ];

  // Auto-detect location on component mount
  useEffect(() => {
    const reverseGeocodeAddress = async (lat, lng) => {
      try {
        // Wait for Google Maps to be fully loaded
        let retries = 0;
        while (!window.google?.maps?.Geocoder && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 500));
          retries++;
        }

        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat, lng } },
            (results, status) => {
              if (status === "OK" && results[0]) {
                setLocation(results[0].formatted_address);
              }
              setIsDetectingLocation(false);
            }
          );
        } else {
          setIsDetectingLocation(false);
        }
      } catch (error) {
        console.error("Error reverse geocoding:", error);
        setIsDetectingLocation(false);
      }
    };

    const detectUserLocation = async () => {
      if (!navigator.geolocation) {
        return;
      }

      setIsDetectingLocation(true);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng } = position.coords;
          setLatitude(lat);
          setLongitude(lng);
          await reverseGeocodeAddress(lat, lng);
        },
        (error) => {
          console.error("Error detecting location:", error);
          setIsDetectingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    };

    detectUserLocation();
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLocationSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch coordinates from place ID
  const fetchCoordinatesFromPlaceId = async (placeId) => {
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const place = new Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });
      
      if (place.location) {
        return {
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  // Fetch location suggestions
  const fetchLocationSuggestions = async (input) => {
    if (!input || input.trim().length < 3) {
      setLocationSuggestions([]);
      return;
    }

    try {
      if (!window.google?.maps) {
        return;
      }

      const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      const request = {
        input: input,
        sessionToken: sessionTokenRef.current,
      };

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const formattedSuggestions = suggestions.map((suggestion) => ({
        description: suggestion.placePrediction.text.toString(),
        placeId: suggestion.placePrediction.placeId,
      }));

      setLocationSuggestions(formattedSuggestions);
    } catch (error) {
      console.error("Autocomplete suggestions error", error);
      setLocationSuggestions([]);
    }
  };

  // Handle location input change
  const handleLocationChange = (value, skipFetch = false) => {
    setLocation(value);
    if (!skipFetch) {
      fetchLocationSuggestions(value);
    }
  };

  const handleOpenCamera = async () => {
    try {
      // Check if the device has any video input devices (camera)
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(
        (device) => device.kind === "videoinput"
      );

      if (!hasVideoInput) {
        setHasCamera(false);
        toast.error("No camera detected on this device.");
        return;
      }

      setHasCamera(true);
      setShowCamera(true);

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasCamera(false);
      toast.error(
        "Unable to access camera. Please check permissions or device settings."
      );
    }
  };

  const validateProfilePic = (file) => {
    if (!file) {
      setProfilePicError("Profile picture is required.");
      return false;
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setProfilePicError("Invalid file type. Only JPG, JPEG, PNG allowed.");
      return false;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setProfilePicError("File is too large. Max size is 10MB.");
      return false;
    }

    setProfilePicError("");
    return true;
  };

  // Capture photo from camera
  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const file = new File([blob], "camera-photo.png", { type: "image/png" });

      if (validateProfilePic(file)) {
        setProfilePic(file);
        setProfilePicUrl(URL.createObjectURL(file));
      } else {
        setProfilePic(null);
        setProfilePicUrl("");
      }
    });

    // Stop camera
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  };

  const countryCodes = [
    { code: "+964", name: "Iraq" }
  ];

  const centerTypes = [
    "Physiotherapy Center",
    "Imaging Center",
    "Lab Center",
  ];

  // Handle file upload
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (validateProfilePic(file)) {
      setProfilePic(file);
      setProfilePicUrl(URL.createObjectURL(file));
    } else {
      setProfilePic(null);
      setProfilePicUrl("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (!centerType) {
      toast.error("Please select a center type to continue.");
      setValidated(true);
      return;
    }

    if (form.checkValidity() === false || !profilePic) {
      e.stopPropagation();
      setValidated(true);
      if (!profilePic) setProfilePicError("Profile picture is required.");
      return;
    }

    if (profilePicError) {
      toast.error(profilePicError);
      return;
    }

    if (!streetAddress.trim()) {
      toast.error("Street is required.");
      setValidated(true);
      return;
    }

    if (latitude === null || longitude === null) {
      toast.error("Please select the center location on the map.");
      setLocationError("Please select the location on the map.");
      setValidated(true);
      return;
    }

    if (!area.trim()) {
      toast.error("Area is required.");
      setValidated(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", profilePic);

      // Upload photo
      const uploadResponse = await axios.post(
        apiUrl('/professionals/photo'),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const profilePictureUrl = uploadResponse.data.data.key;

      // Prepare physiotherapy data
      // Convert single physiotherapy center to hospital_details format for API
      const addressParts = [streetAddress.trim(), area.trim(), location.trim()].filter(Boolean);
      const physiotherapyData = {
        role_id: 8,
        full_name: fullName,
        center: fullName,
        center_type: centerType,
        center_address: addressParts.join(", "),
        street: streetAddress.trim(),
        area: area.trim(),
        online_consultation: onlineConsult === "Yes",
        contact_phone: `${countryCode}${phone}`,
        profile_picture_url: profilePictureUrl,
        short_bio: bio,
        region: region,
      };

      if (location.trim() !== "") {
        physiotherapyData.center_address = addressParts.join(", ");
      }

      if (latitude !== null) {
        physiotherapyData.latitude = latitude;
      }
      if (longitude !== null) {
        physiotherapyData.longitude = longitude;
      }

      await axios.post(apiUrl('/professionals'), physiotherapyData, {
        headers: { "Content-Type": "application/json" },
      });

      console.log('Form submitted successfully!');
      console.log('REACT_APP_FRONTEND_URL:', process.env.REACT_APP_FRONTEND_URL);
      setShowModal(true);

      setFullName("");
      setProfilePic(null);
      setProfilePicUrl("");
      setCenterType("");
      setStreetAddress("");
      setArea("");
      setLocation("");
      setLocationSuggestions([]);
      setLatitude(null);
      setLongitude(null);
      setLocationError("");
      setOnlineConsult("No");
      setPhone("");
      setBio("");
      setRegion("Baghdad");
      setValidated(false);
      setProfilePicError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success("Your details have been saved successfully");
    } catch (error) {
      console.error("Error submitting physiotherapy data:", error);
      toast.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="main-container">
        <main className="main-content">
          <div className="content-wrapper">
            <div className="login-form-container">
              <h2 className="text-center">Physiotherapy/Imaging/Lab Center Details</h2>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="form-group" controlId="centerType">
                  <Form.Label>Center Type</Form.Label>
                  <Form.Select
                    className="form-select"
                    value={centerType}
                    onChange={(e) => setCenterType(e.target.value)}
                    required
                  >
                    <option value="">Select center type</option>
                    {centerTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Center type is required
                  </Form.Control.Feedback>
                </Form.Group>

                    {/* Center Name */}
                    <Form.Group className="form-group" controlId="centerName">
                      <Form.Label>Center Name</Form.Label>
                      <Form.Control
                        className="form-input"
                        type="text"
                        placeholder="Enter center name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Center name is required
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="form-group" controlId="profilePic">
                      <Form.Label>Center Picture</Form.Label>
                      <Row className="align-items-center">
                        <Col xs={3}>
                          <Image
                            src={profilePicUrl || Avatar}
                            roundedCircle
                            width={80}
                            height={80}
                            alt="Profile Preview"
                            style={{
                              objectFit: "cover",
                              border: "2px solid #00bcd4",
                            }}
                          />
                        </Col>
                        <Col>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Upload from Device
                            </Button>
                            <input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              style={{ display: "none" }}
                              onChange={handleProfilePicChange}
                            />
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleOpenCamera}
                            >
                              Open Camera
                            </Button>
                          </div>
                          {profilePicError && (
                            <div className="invalid-feedback d-block">
                              {profilePicError}
                            </div>
                          )}

                          {showCamera && (
                            <div style={{ marginTop: "1rem" }}>
                              {hasCamera ? (
                                <>
                                  <video
                                    ref={videoRef}
                                    style={{ width: "100%", maxHeight: 300 }}
                                  />
                                  <div className="d-flex justify-content-between mt-2">
                                    <Button
                                      variant="success"
                                      onClick={handleCapturePhoto}
                                    >
                                      Capture
                                    </Button>
                                    <Button
                                      variant="danger"
                                      onClick={() => {
                                        const stream = videoRef.current?.srcObject;
                                        if (stream)
                                          stream
                                            .getTracks()
                                            .forEach((track) => track.stop());
                                        setShowCamera(false);
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                  <canvas
                                    ref={canvasRef}
                                    style={{ display: "none" }}
                                  />
                                </>
                              ) : (
                                <div className="text-danger mt-2">
                                  Camera not available on this device.
                                </div>
                              )}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Form.Group>

                    {/* Street Address */}
                    <Form.Group className="form-group" controlId="streetAddress">
                      <Form.Label>Street</Form.Label>
                      <Form.Control
                        className="form-input"
                        type="text"
                        placeholder="Enter street"
                        value={streetAddress}
                        onChange={(e) => setStreetAddress(e.target.value)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Street is required
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Area */}
                    <Form.Group className="form-group" controlId="area">
                      <Form.Label>Area / District</Form.Label>
                      <Form.Control
                        className="form-input"
                        type="text"
                        placeholder="Enter area or district"
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Area is required
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Location */}
                    <Form.Group className="form-group" controlId="location">
                      <Form.Label>Location</Form.Label>
                      <div style={{ position: "relative" }} ref={dropdownRef}>
                        <div style={{ position: "relative" }}>
                          <MapPin
                            size={18}
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#6c757d",
                              zIndex: 1,
                              pointerEvents: "none"
                            }}
                          />
                          <Form.Control
                            className="form-input"
                            type="text"
                            placeholder={isDetectingLocation ? "Detecting your location..." : "Enter center location"}
                            value={location}
                            onChange={(e) => handleLocationChange(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            required
                            disabled={isDetectingLocation}
                            autoComplete="off"
                            style={{
                              paddingLeft: "40px",
                              borderRadius: locationSuggestions.length > 0 ? "8px 8px 0 0" : "8px",
                              border: locationSuggestions.length > 0 ? "1px solid #0d6efd" : "1px solid #ced4da",
                              transition: "all 0.2s ease"
                            }}
                          />
                        </div>
                        {locationSuggestions.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #dee2e6",
                              borderTop: "none",
                              borderRadius: "0 0 8px 8px",
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            {locationSuggestions.map((suggestion, idx) => (
                              <div
                                key={suggestion.placeId}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom: idx === locationSuggestions.length - 1 ? "none" : "1px solid #f0f0f0",
                                  background: "#fff",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  handleLocationChange(suggestion.description, true);
                                  const coords = await fetchCoordinatesFromPlaceId(suggestion.placeId);
                                  if (coords) {
                                    setLatitude(coords.latitude);
                                    setLongitude(coords.longitude);
                                  }
                                  setLocationSuggestions([]);
                                  sessionTokenRef.current = null;
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f8f9fa";
                                  e.currentTarget.style.paddingLeft = "20px";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#fff";
                                  e.currentTarget.style.paddingLeft = "16px";
                                }}
                              >
                                <MapPin
                                  size={18}
                                  style={{
                                    color: "#0d6efd",
                                    marginTop: "2px",
                                    flexShrink: 0
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: "14px",
                                    color: "#212529",
                                    lineHeight: "1.5",
                                    fontWeight: "400"
                                  }}>
                                    {suggestion.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {locationError && (
                        <div className="invalid-feedback d-block">
                          {locationError}
                        </div>
                      )}
                      
                      {/* Map with styled container */}
                      <div 
                        style={{
                          marginTop: "20px",
                          border: "4px solid #0dd0d0",
                          borderRadius: "16px",
                          overflow: "hidden",
                          boxShadow: "0 4px 12px rgba(13,208,208,0.2)"
                        }}
                      >
                        <LocationPicker
                          onLocationSelect={(coords, address) => {
                            setLocation(address || `${coords.lat}, ${coords.lng}`);
                            setLatitude(coords.lat);
                            setLongitude(coords.lng);
                            setLocationError("");
                          }}
                          hideButtons={true}
                          externalPosition={latitude && longitude ? { lat: latitude, lng: longitude } : null}
                        />
                      </div>
                    </Form.Group>

                    {/* Online Consultation */}
                    <Form.Group className="form-group" controlId="onlineConsult">
                      <Form.Label>
                        Are you interested in online consultation?
                      </Form.Label>
                      <div style={{ marginTop: "0.5rem" }}>
                        <ToggleButtonGroup
                          type="radio"
                          name="onlineConsult"
                          value={onlineConsult}
                          onChange={(val) => setOnlineConsult(val)}
                        >
                          <ToggleButton
                            id="physio-online-yes"
                            value="Yes"
                            variant={
                              onlineConsult === "Yes"
                                ? "primary"
                                : "outline-primary"
                            }
                            style={{ minWidth: 80 }}
                          >
                            Yes
                          </ToggleButton>
                          <ToggleButton
                            id="physio-online-no"
                            value="No"
                            variant={
                              onlineConsult === "No" ? "primary" : "outline-primary"
                            }
                            style={{ minWidth: 80 }}
                          >
                            No
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </div>
                    </Form.Group>

                    <Form.Group
                      className="form-group form-group-check"
                      controlId="contactPhone"
                    >
                      <Form.Label>Contact Phone</Form.Label>
                      <Row>
                        <Col xs={5}>
                          <Form.Select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            required
                            style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {countryCodes.map((c, index) => (
                              <option key={`${c.code}-${c.name}-${index}`} value={c.code}>
                                {c.code} - {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col xs={7}>
                          <Form.Control
                            className="form-input"
                            type="tel"
                            placeholder="Enter phone"
                            value={phone}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (!/^[0-9]*$/.test(val)) return;
                              setPhone(val);
                            }}
                            required
                            pattern="^[0-9]{8,15}$"
                          />
                        </Col>
                      </Row>
                      <Form.Control.Feedback type="invalid">
                        Please enter a valid phone number (8–15 digits)
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Region Dropdown */}
                    <Form.Group className="form-group" controlId="region">
                      <Form.Label>Region</Form.Label>
                      <div className="select-wrapper">
                        <Form.Select
                          className="form-select"
                          value={region}
                          onChange={(e) => setRegion(e.target.value)}
                          required
                        >
                          {iraqiRegions.map((regionName) => (
                            <option key={regionName} value={regionName}>
                              {regionName}
                            </option>
                          ))}
                        </Form.Select>
                        <span className="select-icon">&#9662;</span>
                      </div>
                    </Form.Group>

                    {/* Short Bio (optional) */}
                    <Form.Group className="form-group" controlId="bio">
                      <Form.Label>
                        Short Bio / Years of Experience (optional)
                      </Form.Label>
                      <Form.Control
                        className="form-input"
                        as="textarea"
                        rows={3}
                        placeholder="Tell us about your experience"
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                      />
                    </Form.Group>

                    <Button variant="primary" type="submit" className="login-btn" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Submitting...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </Button>
              </Form>
            </div>
          </div>
        </main>
      </div>
      <CustomModal 
        showModal={showModal} 
        onClose={() => setShowModal(false)} 
        autoRedirect={true}
        redirectUrl={process.env.REACT_APP_FRONTEND_URL}
      />
    </>
  );
};

export default PhysiotherapyForm;
